/**
 * Admin API service
 * Handles admin dashboard statistics and platform management
 */

import { api } from "./api";

// Types
export interface UserStats {
  total: number;
  active: number;
  newThisWeek: number;
}

export interface OrganizationStats {
  total: number;
  active: number;
}

export interface ContentStats {
  guides: number;
  publishedGuides: number;
  libraryResources: number;
  featuredResources: number;
  workshops: number;
}

export interface ActivityStats {
  loginsToday: number;
  activeSessions: number;
  apiRequests24h: number;
}

export type HealthStatus = "healthy" | "degraded" | "unhealthy" | "unknown";

export interface HealthStats {
  api: HealthStatus;
  database: HealthStatus;
  externalServices: HealthStatus;
}

export interface AdminStats {
  users: UserStats;
  organizations: OrganizationStats;
  content: ContentStats;
  activity: ActivityStats;
  health: HealthStats;
}

export interface UserWithTenant {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
}

export interface AllUsersResponse {
  users: UserWithTenant[];
  total: number;
  by_tenant: Record<string, number>;
}

export interface AllUsersFilters {
  skip?: number;
  limit?: number;
}

/**
 * Get admin dashboard statistics
 */
export async function getAdminStats(): Promise<AdminStats> {
  const response = await api.get<AdminStats>("/admin/stats");
  return response.data;
}

/**
 * Get all users across all tenants (super admin only)
 */
export async function getAllUsers(
  filters: AllUsersFilters = {},
): Promise<AllUsersResponse> {
  const params = new URLSearchParams();
  if (filters.skip !== undefined) params.append("skip", String(filters.skip));
  if (filters.limit !== undefined)
    params.append("limit", String(filters.limit));

  const queryString = params.toString();
  const url = queryString
    ? `/admin/users/all?${queryString}`
    : "/admin/users/all";
  const response = await api.get<AllUsersResponse>(url);
  return response.data;
}
